import { useLayoutEffect, useRef, type ReactNode } from "react"

/** Align the visible glyphs with adjacent icons rather than relying on font ascenders. */
export function PillLabel({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLSpanElement>(null)
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const canvas = document.createElement("canvas")
    const context = canvas.getContext("2d")
    if (!context) return
    const measure = () => {
      if (!node.isConnected) return
      node.style.translate = "none"
      const style = getComputedStyle(node)
      context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
      const metrics = context.measureText(node.textContent ?? "")
      const range = document.createRange()
      range.selectNodeContents(node)
      const text = range.getBoundingClientRect(),
        box = node.getBoundingClientRect()
      const inkCenter =
        text.top +
        metrics.fontBoundingBoxAscent -
        (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2
      const offset = Math.max(
        -4,
        Math.min(4, box.top + box.height / 2 - inkCenter)
      )
      node.style.translate = `0 ${offset.toFixed(2)}px`
    }
    measure()
    document.fonts.addEventListener("loadingdone", measure)
    const observer = new MutationObserver(measure)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-app-font"],
    })
    return () => {
      observer.disconnect()
      document.fonts.removeEventListener("loadingdone", measure)
    }
  }, [children])
  return (
    <span
      ref={ref}
      data-pill-label
      style={{ display: "inline-block", lineHeight: 1 }}
    >
      {children}
    </span>
  )
}
